import React, { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import { MdAdd, MdOutlineDeleteOutline, MdOutlineFileDownload, MdOutlineFileUpload, MdVisibility } from "react-icons/md"
import api from "../../services/api"
import { useAuth } from "../../contexts/authContext"
import Button from "../../components/Form/Button"
import Input from "../../components/Form/Input"
import Select from "../../components/Form/Select"
import Modal from "../../components/Modal"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import styles from "./templatesManager.module.css"
import TemplateCardMenu from "./TemplateCardMenu"
import WhatsAppTemplateCard, { WhatsAppTemplatePreview } from "../../components/WhatsAppTemplateCard"

const categoryOptions = [
    { id: "ALL", value: "Todas as categorias" },
    { id: "MARKETING", value: "Marketing" },
    { id: "UTILITY", value: "Utilidade" },
    { id: "AUTHENTICATION", value: "Autenticação" },
]

const categoryLabels = {
    MARKETING: "Marketing",
    UTILITY: "Utilidade",
    AUTHENTICATION: "Autenticação",
}

const normalizeName = (value = "") => `${value}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 512)

const getUniqueName = (requestedName, names = []) => {
    const normalizedNames = new Set(names.map(name => normalizeName(name)))
    const baseName = normalizeName(requestedName) || "modelo_da_biblioteca"
    if (!normalizedNames.has(baseName)) return baseName

    let suffix = 2
    while (normalizedNames.has(`${baseName}_${suffix}`)) suffix += 1
    return `${baseName}_${suffix}`
}

const getPortableModel = (model = {}) => ({
    name: normalizeName(model?.name),
    category: model?.category || "UTILITY",
    language: model?.language || "pt_BR",
    format: model?.format || "POSITIONAL",
    content: model?.content || { body: { text: "", examples: [], parameters: [] }, buttons: [] },
})

const TemplateLibrary = ({
    inboxOptions = [],
    existingTemplatesByWaba = {},
    getExistingTemplates = async () => [],
    onDraftAdded = () => {},
    setIsLoading = () => {},
}) => {
    const { permissions } = useAuth()
    const [models, setModels] = useState([])
    const [canManage, setCanManage] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [segment, setSegment] = useState("ALL")
    const [purpose, setPurpose] = useState("ALL")
    const [category, setCategory] = useState("ALL")
    const [search, setSearch] = useState("")
    const [selectedModel, setSelectedModel] = useState(null)
    const [modelToImport, setModelToImport] = useState(null)
    const [importInboxIds, setImportInboxIds] = useState([])
    const [modelToDelete, setModelToDelete] = useState(null)
    const importInputRef = useRef()

    const loadModels = async () => {
        try {
            setIsLoading(true)
            const response = await api.get("/integrations/whatsapp-api/template-library")
            setModels(response?.data?.data || [])
            setCanManage(Boolean(response?.data?.can_manage))
            setLoaded(true)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar a biblioteca de modelos.")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!loaded) loadModels()
        // A biblioteca é global e precisa ser carregada apenas ao abrir a aba.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded])

    useEffect(() => {
        setCanManage(previousValue => previousValue || permissions?.is_admin > 0)
    }, [permissions?.is_admin])

    const segments = useMemo(() => [
        { id: "ALL", value: "Todos os segmentos" },
        ...[...new Set(models.map(model => model?.segment).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b))
            .map(value => ({ id: value, value })),
    ], [models])

    const purposes = useMemo(() => [
        { id: "ALL", value: "Todas as finalidades" },
        ...[...new Set(models
            .filter(model => segment === "ALL" || model?.segment === segment)
            .map(model => model?.purpose).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b))
            .map(value => ({ id: value, value })),
    ], [models, segment])

    useEffect(() => {
        if (purpose !== "ALL" && !purposes.some(option => option.id === purpose)) setPurpose("ALL")
    }, [purpose, purposes])

    const filteredModels = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase()
        return models.filter(model => {
            if (!canManage && !model?.is_published) return false
            if (segment !== "ALL" && model?.segment !== segment) return false
            if (purpose !== "ALL" && model?.purpose !== purpose) return false
            if (category !== "ALL" && model?.category !== category) return false
            if (!normalizedSearch) return true
            return [model?.name, model?.description, model?.segment, model?.purpose, model?.content?.body?.text, ...(model?.tags || [])]
                .filter(Boolean)
                .some(value => `${value}`.toLowerCase().includes(normalizedSearch))
        })
    }, [canManage, category, models, purpose, search, segment])

    const handleOpenImport = (model) => {
        setModelToImport(model)
        setImportInboxIds([])
    }

    const importTargets = useMemo(() => {
        const targetsByWaba = new Map()
        inboxOptions
            .filter(option => importInboxIds.some(inboxId => `${inboxId}` === `${option?.id}`))
            .forEach(option => {
                const wabaId = `${option?.waba_id || ""}`
                if (wabaId && !targetsByWaba.has(wabaId)) {
                    targetsByWaba.set(wabaId, { wabaId, inboxId: `${option.id}` })
                }
            })
        return [...targetsByWaba.values()]
    }, [importInboxIds, inboxOptions])

    const handleAddDraft = async () => {
        if (!modelToImport || !importTargets.length) {
            toast.error("Selecione ao menos uma caixa de entrada para importar o modelo.")
            return
        }

        try {
            setIsLoading(true)
            const template = getPortableModel(modelToImport)
            const startedAt = Date.now()
            const results = await Promise.allSettled(importTargets.map(async (target, index) => {
                const cachedTemplates = existingTemplatesByWaba?.[target.wabaId]
                const targetTemplates = cachedTemplates || await getExistingTemplates(target.wabaId) || []
                const draft = {
                    ...template,
                    template_id: `local-library-${startedAt}-${index}`,
                    name: getUniqueName(template.name, targetTemplates.map(item => item?.name)),
                    status: "DRAFT",
                    waba_id: target.wabaId,
                    inbox_id: target.inboxId,
                    is_local_draft: true,
                }
                const response = await api.post("/integrations/whatsapp-api/templates/drafts", {
                    inbox_id: target.inboxId,
                    waba_id: target.wabaId,
                    templates: [draft],
                })
                return { draft: response?.data?.data?.[0] || draft, wabaId: target.wabaId }
            }))

            const successfulImports = results.filter(result => result.status === "fulfilled")
            const failedImports = results.filter(result => result.status === "rejected")
            successfulImports.forEach(result => onDraftAdded(result.value.draft, result.value.wabaId))

            if (successfulImports.length) {
                toast.success(`Modelo importado em ${successfulImports.length} conta${successfulImports.length === 1 ? "" : "s"} como rascunho!`)
            }
            if (failedImports.length) {
                const firstError = failedImports[0]?.reason
                const failedWabaIds = new Set(results
                    .map((result, index) => result.status === "rejected" ? importTargets[index]?.wabaId : "")
                    .filter(Boolean))
                setImportInboxIds(previousValue => previousValue.filter(inboxId => {
                    const inbox = inboxOptions.find(option => `${option?.id}` === `${inboxId}`)
                    return failedWabaIds.has(`${inbox?.waba_id || ""}`)
                }))
                toast.error(firstError?.response?.data?.message || `Não foi possível importar em ${failedImports.length} conta${failedImports.length === 1 ? "" : "s"}.`)
            } else {
                setModelToImport(null)
                setSelectedModel(null)
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível adicionar o modelo como rascunho.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleImportFile = async (event) => {
        const files = [...(event.target.files || [])]
        event.target.value = ""
        if (!files.length) return

        try {
            const imports = await Promise.all(files.map(async file => JSON.parse(await file.text())))
            setIsLoading(true)
            const responses = await Promise.all(imports.map(payload => api.post("/integrations/whatsapp-api/template-library/import", { payload })))
            const count = responses.reduce((total, response) => total + Number(response?.data?.count || 0), 0)
            toast.success(`${count} modelo${count === 1 ? "" : "s"} importado${count === 1 ? "" : "s"} com sucesso!`)
            setLoaded(false)
        } catch (err) {
            toast.error(err instanceof SyntaxError ? "O arquivo selecionado não é um JSON válido." : (err?.response?.data?.message || "Não foi possível importar os modelos."))
        } finally {
            setIsLoading(false)
        }
    }

    const handleExport = () => {
        const payload = {
            type: "whatsapp_api_template_library",
            version: 1,
            count: models.length,
            templates: models.map(model => ({
                ...getPortableModel(model),
                segment: model?.segment,
                purpose: model?.purpose,
                description: model?.description || "",
                tags: model?.tags || [],
                is_published: model?.is_published !== false,
                sort_order: model?.sort_order || 0,
            })),
        }
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `biblioteca-modelos-whatsapp-${new Date().toISOString().slice(0, 10)}.json`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
    }

    const handleTogglePublished = async (model) => {
        try {
            setIsLoading(true)
            const response = await api.patch(`/integrations/whatsapp-api/template-library/${model?._id}`, {
                ...getPortableModel(model),
                segment: model?.segment,
                purpose: model?.purpose,
                description: model?.description,
                tags: model?.tags,
                sort_order: model?.sort_order,
                is_published: !model?.is_published,
            })
            const updatedModel = response?.data?.data
            setModels(previousValue => previousValue.map(item => item?._id === updatedModel?._id ? updatedModel : item))
            toast.success(updatedModel?.is_published ? "Modelo publicado!" : "Modelo ocultado dos clientes!")
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível atualizar o modelo.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!modelToDelete?._id) return
        try {
            setIsLoading(true)
            await api.delete(`/integrations/whatsapp-api/template-library/${modelToDelete._id}`)
            setModels(previousValue => previousValue.filter(model => model?._id !== modelToDelete._id))
            setModelToDelete(null)
            toast.success("Modelo removido da biblioteca!")
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível remover o modelo.")
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className={styles.library}>
            <Input elementRef={importInputRef} className={styles.hiddenFileInput} type="file" accept="application/json,.json" multiple={true} onChange={handleImportFile} />
            <div className={styles.libraryHeader}>
                <div>
                    <strong>Modelos prontos para começar</strong>
                    <span>Encontre uma base, importe como rascunho e personalize antes de enviar à Meta.</span>
                </div>
                {canManage && <div className={styles.libraryAdminActions}>
                    <Button variant="secondary" onClick={handleExport} disabled={!models.length}><MdOutlineFileDownload /> Exportar biblioteca</Button>
                    <Button variant="primary" onClick={() => importInputRef.current?.click()}><MdOutlineFileUpload /> Importar JSON</Button>
                </div>}
            </div>

            <div className={styles.libraryToolbar}>
                <Select name="Segmento" options={segments} selected={segment} onChange={setSegment} />
                <Select name="Finalidade" options={purposes} selected={purpose} onChange={setPurpose} />
                <Select name="Categoria Meta" options={categoryOptions} selected={category} onChange={setCategory} />
                <Input type="search" placeholder="Buscar modelo..." value={search} onChange={event => setSearch(event.target.value)} />
            </div>

            <div className={styles.libraryCount}>{filteredModels.length} modelo{filteredModels.length === 1 ? "" : "s"}</div>
            <div className={styles.libraryGrid}>
                {filteredModels.map(model => (
                    <WhatsAppTemplateCard
                        key={model?._id || model?.name}
                        template={model}
                        title={model?.name || "Modelo"}
                        className={!model?.is_published ? styles.libraryCardHidden : ""}
                        onClick={() => setSelectedModel(model)}
                        header={
                            <div className={styles.libraryCardHeader}>
                                <div className={styles.libraryCardTags}>
                                    <span>{model?.segment}</span>
                                    <span>{model?.purpose}</span>
                                </div>
                                <div className={styles.libraryCardHeaderActions}>
                                    <small>{categoryLabels[model?.category] || model?.category} · {model?.language}</small>
                                    <TemplateCardMenu actions={[
                                        { label: "Visualizar", onClick: () => setSelectedModel(model) },
                                        { label: "Importar modelo", onClick: () => handleOpenImport(model) },
                                        ...(canManage ? [
                                            { label: model?.is_published ? "Ocultar dos clientes" : "Publicar para clientes", onClick: () => handleTogglePublished(model) },
                                            { label: "Excluir", onClick: () => setModelToDelete(model) },
                                        ] : []),
                                    ]} />
                                </div>
                            </div>
                        }
                        description={model?.description || model?.content?.body?.text}
                    />
                ))}
            </div>
            {!filteredModels.length && <div className={styles.libraryEmpty}>{loaded ? "Nenhum modelo encontrado para os filtros selecionados." : "Carregando biblioteca..."}</div>}

            {selectedModel && <Modal icon={<MdVisibility />} title={selectedModel?.name} description={`${selectedModel?.segment} · ${selectedModel?.purpose}`} closeModal={() => setSelectedModel(null)} fitContent={true} heightFitContent={true}>
                <div className={styles.libraryPreview}>
                    <WhatsAppTemplatePreview template={selectedModel} />
                    <Button variant="primary" onClick={() => handleOpenImport(selectedModel)}><MdAdd /> Importar modelo</Button>
                </div>
            </Modal>}

            {modelToImport && <Modal
                icon={<MdOutlineFileUpload />}
                title="Importar modelo"
                description={modelToImport?.name}
                closeModal={() => setModelToImport(null)}
                fitContent={true}
                heightFitContent={true}
            >
                <div className={styles.libraryImportModal}>
                    <div className={styles.libraryImportSummary}>
                        <span>O modelo será criado como rascunho para você personalizar e revisar antes de enviá-lo à Meta.</span>
                        <strong>{modelToImport?.segment} · {modelToImport?.purpose}</strong>
                    </div>
                    <Select
                        multiple={true}
                        name="Caixas de entrada de destino"
                        placeholder="Buscar caixa..."
                        options={inboxOptions}
                        selected={importInboxIds}
                        onChange={setImportInboxIds}
                        disabled={!inboxOptions.length}
                    />
                    {importInboxIds.length > 0 && <div className={styles.libraryImportDestinations}>
                        <strong>{importInboxIds.length} caixa{importInboxIds.length === 1 ? "" : "s"} selecionada{importInboxIds.length === 1 ? "" : "s"}</strong>
                        <span>{importTargets.length} conta{importTargets.length === 1 ? "" : "s"} WhatsApp receberá{importTargets.length === 1 ? "" : "ão"} o modelo.</span>
                    </div>}
                    <small>Caixas da mesma conta compartilham os templates, então a importação será feita apenas uma vez por conta. Em caso de nome repetido, um sufixo será adicionado.</small>
                    <div className={styles.libraryImportActions}>
                        <Button variant="secondary" type="button" onClick={() => setModelToImport(null)}>Cancelar</Button>
                        <Button variant="primary" type="button" onClick={handleAddDraft} disabled={!importTargets.length}>
                            <MdOutlineFileUpload /> Importar como rascunho
                        </Button>
                    </div>
                </div>
            </Modal>}

            {modelToDelete && <ModalConfirm
                closeModal={() => setModelToDelete(null)}
                icon={<MdOutlineDeleteOutline />}
                title="Excluir modelo da biblioteca"
                description={modelToDelete?.name}
                text="O modelo deixará de aparecer para todos os clientes. Os rascunhos já adicionados às contas não serão removidos."
                onSubmit={handleDelete}
            />}
        </div>
    )
}

export default TemplateLibrary
